import { useMemo } from "react";
import { Badge, Button, Form, Table } from "react-bootstrap";
import Loader from "./Loader";
import Message from "./Message";
import {
  useAdminGetCatalogMakesQuery,
  useAdminListModelsQuery,
  useAdminListYearsQuery,
  useAdminListTrimsQuery,
  useAdminListEnginesQuery,
} from "../slices/productsApiSlice";

const blankFitment = () => ({
  make: "",
  model: "",
  year: null,
  trim: "",
  engine: "",
  notes: "",
});

function FitmentRow({ row, idx, onChangeRow, onDeleteRow, onDuplicateRow }) {
  const make = row.make || "";
  const model = row.model || "";
  const year = row.year || "";

  const { data: makes = [], isLoading: lm, error: em } = useAdminGetCatalogMakesQuery();

  const {
    data: models = [],
    isLoading: lmo,
    error: emo,
  } = useAdminListModelsQuery({ make }, { skip: !make });

  const {
    data: years = [],
    isLoading: ly,
    error: ey,
  } = useAdminListYearsQuery({ make, model }, { skip: !(make && model) });

  const {
    data: trims = [],
    isLoading: lt,
    error: et,
  } = useAdminListTrimsQuery(
    { make, model, year },
    { skip: !(make && model && year) }
  );

  const {
    data: engines = [],
    isLoading: le,
    error: ee,
  } = useAdminListEnginesQuery(
    { make, model, year },
    { skip: !(make && model && year) }
  );

  const activeMakes = useMemo(() => makes.filter((m) => m.active !== false), [makes]);
  const activeModels = useMemo(() => models.filter((m) => m.active !== false), [models]);

  const activeYears = useMemo(() => {
    const ys = years
      .filter((y) => y.active !== false)
      .map((y) => Number(y.year))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);
    return ys;
  }, [years]);

  const activeTrims = useMemo(
    () => trims.filter((t) => t.active !== false).map((t) => t.trim),
    [trims]
  );

  const activeEngines = useMemo(
    () => engines.filter((e) => e.active !== false).map((e) => e.engine),
    [engines]
  );

  const set = (patch) => onChangeRow(idx, { ...row, ...patch });
  const anyError = em || emo || ey || et || ee;
  const isComplete = Boolean(make && model);

  return (
    <>
      {anyError && (
        <tr>
          <td colSpan={8}>
            <Message variant="danger" className="mb-0">
              {anyError?.data?.message || anyError?.error || "Catalog error"}
            </Message>
          </td>
        </tr>
      )}

      <tr>
        <td className="text-muted fw-semibold" style={{ width: 60 }}>
          #{idx + 1}
        </td>

        <td style={{ minWidth: 150 }}>
          {lm && <Loader />}
          <Form.Select
            value={make}
            onChange={(e) =>
              set({
                make: e.target.value,
                model: "",
                year: null,
                trim: "",
                engine: "",
                notes: row.notes || "",
              })
            }
          >
            <option value="">Select make…</option>
            {activeMakes.map((m) => (
              <option key={m.make || m._id} value={m.make}>
                {m.make}
              </option>
            ))}
          </Form.Select>
        </td>

        <td style={{ minWidth: 170 }}>
          {lmo && make && <Loader />}
          <Form.Select
            value={model}
            disabled={!make}
            onChange={(e) =>
              set({
                model: e.target.value,
                year: null,
                trim: "",
                engine: "",
              })
            }
          >
            <option value="">Select model…</option>
            {activeModels.map((m) => (
              <option key={m._id} value={m.model}>
                {m.model}
              </option>
            ))}
          </Form.Select>
        </td>

        <td style={{ minWidth: 130 }}>
          {ly && make && model && <Loader />}
          <Form.Select
            value={row.year ?? ""}
            disabled={!(make && model)}
            onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : null;
              set({ year: v, trim: "", engine: "" });
            }}
          >
            <option value="">(Any year)</option>
            {activeYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Form.Select>
        </td>

        <td style={{ minWidth: 160 }}>
          {lt && make && model && year && <Loader />}
          <Form.Select
            value={row.trim || ""}
            disabled={!(make && model && year)}
            onChange={(e) => set({ trim: e.target.value })}
          >
            <option value="">(Any trim)</option>
            {activeTrims.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Form.Select>
        </td>

        <td style={{ minWidth: 160 }}>
          {le && make && model && year && <Loader />}
          <Form.Select
            value={row.engine || ""}
            disabled={!(make && model && year)}
            onChange={(e) => set({ engine: e.target.value })}
          >
            <option value="">(Any engine)</option>
            {activeEngines.map((en) => (
              <option key={en} value={en}>
                {en}
              </option>
            ))}
          </Form.Select>
        </td>

        <td style={{ minWidth: 190 }}>
          <Form.Control
            value={row.notes || ""}
            onChange={(e) => set({ notes: e.target.value })}
            placeholder="Optional notes"
          />
          <div className="mt-1">
            <Badge bg={isComplete ? "success" : "warning"} text={isComplete ? undefined : "dark"}>
              {isComplete ? "Ready" : "Select make + model"}
            </Badge>
          </div>
        </td>

        <td style={{ width: 150 }}>
          <div className="d-grid gap-1">
            <Button variant="outline-secondary" size="sm" onClick={() => onDuplicateRow(idx)}>
              Duplicate
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => onDeleteRow(idx)}>
              Delete
            </Button>
          </div>
        </td>
      </tr>
    </>
  );
}

export default function FitmentEditor({ value = [], onChange }) {
  const rows = useMemo(() => Array.isArray(value) ? value : [], [value]);

  const summary = useMemo(() => {
    const total = rows.length;
    const ready = rows.filter((r) => r?.make && r?.model).length;
    const withYear = rows.filter((r) => Number.isFinite(Number(r?.year))).length;
    return { total, ready, withYear };
  }, [rows]);

  const addRow = () => {
    onChange([...rows, blankFitment()]);
  };

  const addThreeRows = () => {
    onChange([...rows, blankFitment(), blankFitment(), blankFitment()]);
  };

  const onChangeRow = (idx, next) => {
    const copy = [...rows];
    copy[idx] = next;
    onChange(copy);
  };

  const onDeleteRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  const onDuplicateRow = (idx) => {
    const copy = [...rows];
    const source = copy[idx] || blankFitment();
    copy.splice(idx + 1, 0, { ...source });
    onChange(copy);
  };

  const clearAll = () => onChange([]);

  return (
    <div className="border rounded p-3 bg-light-subtle">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div>
          <h5 className="m-0">Vehicle Fitments</h5>
          <div className="text-muted small">
            Add compatible vehicles so customers can quickly confirm fit.
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Badge bg="dark">Total: {summary.total}</Badge>
          <Badge bg="success">Ready: {summary.ready}</Badge>
          <Badge bg="info">With year: {summary.withYear}</Badge>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <Button size="sm" onClick={addRow}>
          + Add fitment
        </Button>
        <Button size="sm" variant="outline-primary" onClick={addThreeRows}>
          + Add 3 rows
        </Button>
        <Button size="sm" variant="outline-danger" onClick={clearAll} disabled={rows.length === 0}>
          Clear all
        </Button>
      </div>

      <Table bordered hover size="sm" responsive className="m-0 align-middle bg-white">
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Make</th>
            <th>Model</th>
            <th>Year</th>
            <th>Trim</th>
            <th>Engine</th>
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center py-4 text-muted">
                No fitments yet. Click <strong>Add fitment</strong> to start.
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <FitmentRow
                key={idx}
                row={row}
                idx={idx}
                onChangeRow={onChangeRow}
                onDeleteRow={onDeleteRow}
                onDuplicateRow={onDuplicateRow}
              />
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
}
